// Gift with purchase. One free gift while a qualifying product is in the cart. -- SG 20261019

interface GwpMessages {
	gift: string;
	soldOut: string;
	giftAdded: string;
	giftRemoved: string;
	giftRemovedSoldOut: string;
}

interface GwpConfig {
	variantId: number;
	productId: number;
	available: boolean;
	qualifyingProductIds: number[];
	qualifyingCount: number;
	collectionHandle: string;
	startsAt: string;
	messages: GwpMessages;
	checkOnLoad: boolean;
}

interface CartLine {
	key: string;
	product_id: number;
	variant_id: number;
	quantity: number;
	final_line_price: number;
}

interface CartState {
	token?: string;
	item_count?: number;
	items?: CartLine[];
}

interface CartWriteResponse extends CartState {
	sections?: Record<string, string>;
}

interface PreviewCartElement extends HTMLElement {
	parentSectionId?: string;
	refreshPreviewCart?: (data: { sections?: Record<string, string> }) => void;
}

interface FocusMemory {
	root: Element;
	selector: string | null;
}

declare global {
	interface Window {
		gwpConfig?: GwpConfig;
	}
}

const DEBOUNCE_MS = 150;
const CHECKOUT_TIMEOUT_MS = 2000;
const ISO_WITH_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const FOCUSABLE = "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])";

let cachedQualifyingIds: Set<number> | null = null;
let runPromise: Promise<void> | null = null;
let queued = false;
let ignoreOwnUpdate = false;
let soldOut = false;
let soldOutAnnounced = false;
let priceBlocked = false;
let startInvalidWarned = false;
let checkoutBypass = false;
let checkoutGuarding = false;
let debounceTimer: number | null = null;
let announceToken = 0;

// Blank starts now. Anything else must be ISO 8601 with an offset, rechecked on each trigger. -- SG 20261019
function isActive(cfg: GwpConfig): boolean {
	const raw = (cfg.startsAt || "").trim();
	if (raw === "") return true;
	if (!ISO_WITH_OFFSET.test(raw) || Number.isNaN(Date.parse(raw))) {
		if (!startInvalidWarned) {
			startInvalidWarned = true;
			console.warn("[GWP] startsAt is not a valid ISO 8601 time with an offset, so the gift will not start");
		}
		return false;
	}
	return Date.parse(raw) <= Date.now();
}

function drawerSectionId(): string | undefined {
	const preview = document.querySelector("preview-cart");
	if (!(preview instanceof HTMLElement)) return undefined;
	const host = preview as PreviewCartElement;
	if (host.parentSectionId) return host.parentSectionId;
	const section = preview.closest("[id^='shopify-section-']");
	if (!section?.id.startsWith("shopify-section-")) return undefined;
	return section.id.slice("shopify-section-".length);
}

function sectionBody(): { sections?: string; sections_url: string } {
	const body: { sections?: string; sections_url: string } = {
		sections_url: window.location.pathname,
	};
	// Checkout navigates away, so skip the drawer render to fit inside the checkout wait. -- SG 20261019
	if (checkoutGuarding) return body;
	const id = drawerSectionId();
	if (id) body.sections = id;
	return body;
}

async function readJson(res: Response): Promise<unknown> {
	return res.json();
}

async function fetchCart(): Promise<CartState> {
	const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
	if (!res.ok) throw new Error(`cart.js ${res.status}`);
	return (await readJson(res)) as CartState;
}

function cartLines(cart: CartState): CartLine[] {
	return Array.isArray(cart.items) ? cart.items : [];
}

function giftLines(cart: CartState, cfg: GwpConfig): CartLine[] {
	return cartLines(cart).filter((item) => item.variant_id === cfg.variantId);
}

function hasQualifying(cart: CartState, cfg: GwpConfig, ids: Set<number>): boolean {
	return cartLines(cart).some((item) => item.variant_id !== cfg.variantId && ids.has(item.product_id));
}

// Page-lifetime cache. An empty products.json body means no extra ids. -- SG 20261019
async function qualifyingIds(cfg: GwpConfig): Promise<Set<number>> {
	if (cachedQualifyingIds) return cachedQualifyingIds;
	const ids = new Set<number>(cfg.qualifyingProductIds);
	if (cfg.qualifyingCount <= cfg.qualifyingProductIds.length) {
		cachedQualifyingIds = ids;
		return ids;
	}
	const handle = encodeURIComponent(cfg.collectionHandle);
	for (let page = 1; page <= 20; page += 1) {
		const res = await fetch(`/collections/${handle}/products.json?limit=250&page=${page}`);
		if (!res.ok) break;
		const data = (await readJson(res)) as { products?: { id: number }[] };
		const products = data.products || [];
		if (products.length === 0) break;
		for (const product of products) ids.add(product.id);
		if (products.length < 250) break;
	}
	cachedQualifyingIds = ids;
	return ids;
}

async function postJson(url: string, payload: Record<string, unknown>): Promise<{ ok: boolean; status: number; data: CartWriteResponse }> {
	const res = await fetch(url, {
		method: "POST",
		headers: { "Content-Type": "application/json", Accept: "application/json" },
		body: JSON.stringify({ ...payload, ...sectionBody() }),
	});
	let data: CartWriteResponse = {};
	try {
		data = (await readJson(res)) as CartWriteResponse;
	} catch {
		data = {};
	}
	return { ok: res.ok, status: res.status, data };
}

function refresh(data: { sections?: Record<string, string> }): void {
	const preview = document.querySelector("preview-cart");
	if (!(preview instanceof HTMLElement)) return;
	const host = preview as PreviewCartElement;
	if (!data.sections || typeof host.refreshPreviewCart !== "function") return;
	ignoreOwnUpdate = true;
	try {
		host.refreshPreviewCart(data);
	} finally {
		ignoreOwnUpdate = false;
	}
	if (checkoutGuarding) markCheckoutBusy();
}

// A re-render during the checkout check replaces the button, so reapply the busy state. -- SG 20261019
function markCheckoutBusy(): void {
	document.querySelectorAll<HTMLButtonElement>('button[name="checkout"]').forEach((button) => {
		button.disabled = true;
		button.setAttribute("aria-busy", "true");
	});
}

async function refreshSections(): Promise<void> {
	const id = drawerSectionId();
	if (!id) return;
	const res = await fetch(`/?sections=${encodeURIComponent(id)}`);
	if (!res.ok) return;
	const sections = (await readJson(res)) as Record<string, string>;
	refresh({ sections });
}

function announce(message: string): void {
	const el = document.getElementById("gwp-status");
	if (!el) return;
	announceToken += 1;
	const token = announceToken;
	el.textContent = "";
	window.setTimeout(() => {
		if (token !== announceToken) return;
		el.textContent = message;
	}, 50);
}

function fillMessage(template: string, cfg: GwpConfig): string {
	return template.split("__GIFT__").join(cfg.messages.gift).split("__SOLD_OUT__").join(cfg.messages.soldOut);
}

function cssEscape(value: string): string {
	if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(value);
	return value.replace(/["\\]/g, "\\$&");
}

function rememberFocus(): FocusMemory | null {
	const active = document.activeElement;
	if (!(active instanceof HTMLElement)) return null;
	const root = active.closest("preview-cart");
	if (!root) return null;
	let selector: string | null = null;
	if (active.id) {
		selector = `#${cssEscape(active.id)}`;
	} else {
		const key = active.closest("[data-item-key]")?.getAttribute("data-item-key");
		const name = active.getAttribute("name");
		if (key && name) selector = `[data-item-key="${cssEscape(key)}"] [name="${cssEscape(name)}"]`;
	}
	return { root, selector };
}

function restoreFocus(memory: FocusMemory | null): void {
	if (!memory || !memory.root.isConnected) return;
	const match = memory.selector ? memory.root.querySelector(memory.selector) : null;
	const target = match instanceof HTMLElement ? match : memory.root.querySelector(FOCUSABLE);
	if (target instanceof HTMLElement) target.focus();
}

function ensureSoldOutLine(message: string): void {
	if (!message) return;
	document.querySelectorAll("preview-cart").forEach((cart) => {
		if (cart.querySelector(".preview-cart__gwp-sold-out")) return;
		const items = cart.querySelector(".preview-cart__items");
		if (!items) return;
		const line = document.createElement("p");
		line.className = "preview-cart__gwp-sold-out content-sm font-avenir";
		line.textContent = message;
		items.appendChild(line);
	});
}

function noteSoldOut(cfg: GwpConfig): void {
	ensureSoldOutLine(cfg.messages.soldOut);
	if (soldOutAnnounced) return;
	soldOutAnnounced = true;
	announce(cfg.messages.soldOut);
}

// One extra read when a third-party cart write may have landed between our read and write. -- SG 20261019
async function confirmWrite(before: CartState, delta: number, response: CartState, allowRecheck: boolean): Promise<void> {
	if (!allowRecheck) return;
	let after = response;
	if (typeof after.item_count !== "number" || typeof after.token !== "string") after = await fetchCart();
	const raced = after.token !== before.token || after.item_count !== (before.item_count || 0) + delta;
	if (raced) await reconcileOnce(false);
}

async function applyUpdates(
	updates: Record<string, number>,
	before: CartState,
	delta: number,
	focus: FocusMemory | null,
	allowRecheck: boolean
): Promise<boolean> {
	const result = await postJson("/cart/update.js", { updates });
	if (!result.ok) {
		console.warn("[GWP] cart update failed", result.status);
		return false;
	}
	refresh(result.data);
	restoreFocus(focus);
	await confirmWrite(before, delta, result.data, allowRecheck);
	return true;
}

function removalUpdates(lines: CartLine[]): { updates: Record<string, number>; delta: number } {
	const updates: Record<string, number> = {};
	let delta = 0;
	for (const line of lines) {
		updates[line.key] = 0;
		delta -= line.quantity;
	}
	return { updates, delta };
}

async function addGift(cfg: GwpConfig, before: CartState, focus: FocusMemory | null, allowRecheck: boolean): Promise<void> {
	const result = await postJson("/cart/add.js", { items: [{ id: cfg.variantId, quantity: 1 }] });
	if (result.status === 422) {
		soldOut = true;
		await refreshSections();
		noteSoldOut(cfg);
		return;
	}
	if (!result.ok) {
		console.warn("[GWP] cart add failed", result.status);
		return;
	}
	// At checkout the add response carries the line price, which saves a cart read before navigating. -- SG 20261019
	const afterAdd = checkoutGuarding ? result.data : await fetchCart();
	const added = giftLines(afterAdd, cfg);
	if (added.length === 0) return;
	if (added.some((line) => line.final_line_price > 0)) {
		priceBlocked = true;
		const removal = removalUpdates(added);
		const removed = await applyUpdates(removal.updates, before, 0, focus, allowRecheck);
		if (removed) announce(fillMessage(cfg.messages.giftRemoved, cfg));
		return;
	}
	refresh(result.data);
	restoreFocus(focus);
	announce(fillMessage(cfg.messages.giftAdded, cfg));
	if (!checkoutGuarding) await confirmWrite(before, 1, afterAdd, allowRecheck);
}

async function reconcileOnce(allowRecheck: boolean): Promise<void> {
	const cfg = window.gwpConfig;
	if (!cfg || !cfg.variantId) return;
	const active = isActive(cfg);
	const ids = await qualifyingIds(cfg);
	const cart = await fetchCart();
	const gifts = giftLines(cart, cfg);
	const qualifying = hasQualifying(cart, cfg, ids);

	if (gifts.some((line) => line.final_line_price > 0)) {
		priceBlocked = true;
		const removal = removalUpdates(gifts);
		const removed = await applyUpdates(removal.updates, cart, removal.delta, rememberFocus(), allowRecheck);
		if (removed) announce(fillMessage(cfg.messages.giftRemoved, cfg));
		return;
	}

	if (!active || !qualifying) {
		if (gifts.length === 0) return;
		const removal = removalUpdates(gifts);
		const removed = await applyUpdates(removal.updates, cart, removal.delta, rememberFocus(), allowRecheck);
		if (removed) announce(fillMessage(cfg.messages.giftRemoved, cfg));
		return;
	}

	// Carts do not reserve stock, so a sold-out gift left in the cart would fail at checkout. -- SG 20261019
	if (!cfg.available || soldOut) {
		if (gifts.length > 0) {
			const removal = removalUpdates(gifts);
			const removed = await applyUpdates(removal.updates, cart, removal.delta, rememberFocus(), allowRecheck);
			if (removed) {
				soldOutAnnounced = true;
				announce(fillMessage(cfg.messages.giftRemovedSoldOut, cfg));
			}
		}
		noteSoldOut(cfg);
		return;
	}

	if (gifts.length === 0) {
		if (priceBlocked) return;
		await addGift(cfg, cart, rememberFocus(), allowRecheck);
		return;
	}

	const only = gifts[0];
	if (gifts.length === 1 && only && only.quantity === 1) return;

	// Line keys, so a duplicate of the same variant can be zeroed without raising the kept line. -- SG 20261019
	const updates: Record<string, number> = {};
	let delta = 0;
	gifts.forEach((line, index) => {
		const next = index === 0 ? 1 : 0;
		updates[line.key] = next;
		delta += next - line.quantity;
	});
	await applyUpdates(updates, cart, delta, rememberFocus(), allowRecheck);
}

function reconcile(): Promise<void> {
	if (runPromise) {
		queued = true;
		return runPromise;
	}
	const promise = (async () => {
		try {
			do {
				queued = false;
				try {
					await reconcileOnce(true);
				} catch (err) {
					console.warn("[GWP] reconcile failed", err);
				}
			} while (queued);
		} finally {
			runPromise = null;
		}
	})();
	runPromise = promise;
	return promise;
}

function schedule(): void {
	if (debounceTimer) window.clearTimeout(debounceTimer);
	debounceTimer = window.setTimeout(() => {
		debounceTimer = null;
		void reconcile();
	}, DEBOUNCE_MS);
}

function findCheckoutButton(section: Element | null, formId: string): HTMLButtonElement | null {
	const scope: ParentNode = section || document;
	if (formId) {
		const named = scope.querySelector(`button[name="checkout"][form="${cssEscape(formId)}"]`);
		if (named instanceof HTMLButtonElement) return named;
	}
	const inside = scope.querySelector('button[name="checkout"]');
	return inside instanceof HTMLButtonElement ? inside : null;
}

function checkoutButton(form: HTMLFormElement, submitter: EventTarget | null): HTMLButtonElement | null {
	if (submitter instanceof HTMLButtonElement && submitter.name === "checkout" && submitter.form === form) return submitter;
	return findCheckoutButton(null, form.id);
}

// Re-find the button after the drawer re-render, then submit with the bypass set. -- SG 20261019
async function guardCheckout(button: HTMLButtonElement): Promise<void> {
	if (checkoutGuarding) return;
	checkoutGuarding = true;
	const formId = button.getAttribute("form") || button.form?.id || "";
	const section = button.closest(".section-cart, .section-preview-cart");
	const wasDisabled = button.disabled;
	const busy = button.getAttribute("aria-busy");
	button.disabled = true;
	button.setAttribute("aria-busy", "true");
	try {
		try {
			await Promise.race([
				reconcile(),
				new Promise<void>((resolve) => {
					window.setTimeout(resolve, CHECKOUT_TIMEOUT_MS);
				}),
			]);
		} catch (err) {
			console.warn("[GWP] checkout reconcile failed", err);
		}
		const fresh = findCheckoutButton(section, formId) || button;
		document.querySelectorAll<HTMLButtonElement>('button[name="checkout"][aria-busy="true"]').forEach((other) => {
			other.disabled = false;
			other.removeAttribute("aria-busy");
		});
		if (fresh.isConnected) {
			fresh.disabled = wasDisabled;
			if (busy === null) fresh.removeAttribute("aria-busy");
			else fresh.setAttribute("aria-busy", busy);
		}
		const form = (fresh.isConnected ? fresh.form : null) || button.form;
		if (!form) return;
		checkoutBypass = true;
		try {
			if (fresh.isConnected) form.requestSubmit(fresh);
			else form.requestSubmit();
		} finally {
			checkoutBypass = false;
		}
	} finally {
		checkoutGuarding = false;
	}
}

function onCheckoutClick(event: MouseEvent): void {
	if (checkoutBypass) return;
	if (!(event.target instanceof Element)) return;
	const button = event.target.closest('button[name="checkout"]');
	if (!(button instanceof HTMLButtonElement)) return;
	event.preventDefault();
	void guardCheckout(button);
}

function onCheckoutSubmit(event: Event): void {
	if (checkoutBypass) return;
	if (!(event.target instanceof HTMLFormElement)) return;
	const submitter = "submitter" in event ? (event as SubmitEvent).submitter : null;
	const button = checkoutButton(event.target, submitter);
	if (!button || button.form !== event.target) return;
	event.preventDefault();
	void guardCheckout(button);
}

function initGiftWithPurchase(): void {
	if (!window.gwpConfig) return;
	// Cast so Window.Shopify stays untyped for the rest of the build. -- SG 20261019
	const shopify = (window as { Shopify?: { designMode?: boolean } }).Shopify;
	if (shopify?.designMode) return;
	if (window.gwpConfig.checkOnLoad) {
		if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => schedule(), { once: true });
		else schedule();
	}
	document.addEventListener("cart:updated", () => {
		if (ignoreOwnUpdate) return;
		schedule();
	});
	window.addEventListener("focus", () => schedule());
	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "visible") schedule();
	});
	document.addEventListener("click", onCheckoutClick, true);
	document.addEventListener("submit", onCheckoutSubmit, true);
}

initGiftWithPurchase();

export {};
